'use client'

import React, { useState, useCallback, useEffect } from 'react'
import axios from 'axios'
import { trackEvent, getCommonEventProperties } from '@acko-tech/building-blocks.ui.acko-analytics'
import { useGICar } from '@context/GICarContext/GICarContext'
import { validateRegistrationNumber, formatRegistrationNumber } from '@utils/giCar/registrationValidation'
import { RegistrationNumberProps } from '@interfaces/giCar'
import FormWidget from './FormWidget'

const RegistrationNumber: React.FC<RegistrationNumberProps> = ({
  onSuccess,
  onError,
  mobileNumber = '',
  autoFillNumber = '',
  autoSubmit = false,
  regexPatternsData = [],
  formData,
}) => {
  const giCarContext = useGICar()
  const [isDirty, setIsDirty] = useState(false)
  const [localError, setLocalError] = useState('')
  const getCommonEventProps = (registrationNumber: string) => ({
    ...getCommonEventProperties('seo'),
    header_name: 'Home',
    product: 'auto-car',
    funnel: 'entry',
    registration_number: registrationNumber,
  })

  useEffect(() => {
    if (autoFillNumber && !giCarContext.registrationNumber) {
      const formatted = formatRegistrationNumber(autoFillNumber)
      giCarContext.setRegistrationNumber(formatted)
      const validation = validateRegistrationNumber(formatted, regexPatternsData)
      if (validation.valid && autoSubmit) {
        setTimeout(() => {
          void handleSubmit(formatted, 'auto')
        }, 500)
      }
    }
  }, [autoFillNumber, autoSubmit, regexPatternsData])

  const handleChangeHandler = useCallback(
    (value: string) => {
      const formatted = formatRegistrationNumber(value)
      giCarContext.setRegistrationNumber(formatted)
      setLocalError('')
      giCarContext.setErrorMessage('')
      if (isDirty) {
        const validation = validateRegistrationNumber(formatted, regexPatternsData)
        if (!validation.valid) {
          setLocalError(validation.error || '')
        }
      }
    },
    [isDirty, giCarContext, regexPatternsData]
  )

  const handleBlurHandler = useCallback(() => {
    setIsDirty(true)

    const validation = validateRegistrationNumber(giCarContext.registrationNumber, regexPatternsData)
    if (!validation.valid) {
      setLocalError(validation.error || '')
      giCarContext.setErrorMessage(validation.error || '')
    } else {
      setLocalError('')
      giCarContext.setErrorMessage('')
    }
  }, [giCarContext, regexPatternsData])

  const handleSubmit = useCallback(
    async (regNumber?: string, source: 'cta' | 'enter' | 'auto' = 'cta') => {
      const numberToSubmit = regNumber || giCarContext.registrationNumber
      if (source === 'cta') {
        try {
          void trackEvent('tap_registration_number_cta', getCommonEventProps(numberToSubmit))
        } catch (error) {
          console.error('Failed to track tap_registration_number_cta event:', error)
        }
      }
      const validation = validateRegistrationNumber(numberToSubmit, regexPatternsData)
      if (!validation.valid) {
        setLocalError(validation.error || '')
        giCarContext.setErrorMessage(validation.error || '')
        setIsDirty(true)

        if (source === 'cta') {
          try {
            void trackEvent('error_triggered_on_cta', {
              ...getCommonEventProps(numberToSubmit),
              error_message: validation.error || '',
              context: 'client error',
            })
          } catch (error) {
            console.error('Failed to track error_triggered_on_cta event:', error)
          }
        }

        onError?.({
          type: 'validation_error',
          message: validation.error,
        })
        return
      }
      try {
        giCarContext.setIsLoading(true)
        setLocalError('')
        const proposalAPIUrl = '/motororchestrator/api/v2/proposals'
        const TIMEOUT_6000 = 15000
        const illogicalFlowConst = [
          'ILLOGICAL_FLOW_POLICY_EXISTS',
          'ILLOGICAL_FLOW_PAYMENT_DONE',
          'ILLOGICAL_FLOW_POLICY_EXPIRED_RENEWAL_DUE',
          'ILLOGICAL_FLOW_UNACCEPTABLE_ASSET',
        ]

        await axios
          .request({
            method: 'post',
            url: proposalAPIUrl,
            headers: {
              'X-Landing-URL': typeof window !== 'undefined' ? window.location?.href : '/',
              'X-Landing-Path': '/',
              'x-app-name': 'frontend-seo',
              'x-platform': 'mweb',
            },
            data: {
              registration_number: numberToSubmit,
              mobile_no: mobileNumber || '',
              origin: 'acko',
              product: 'car',
              is_new: false,
            },
            timeout: TIMEOUT_6000,
          })
          .then(res => {
            if (res.data?.error_code) {
              const isIllogicalFlow = illogicalFlowConst.filter(key => key === res.data?.error_code).length > 0

              if (isIllogicalFlow) {
                const rawErrorContext = res.data.error_context
                const errorContext =
                  typeof rawErrorContext === 'object' && rawErrorContext !== null ? rawErrorContext : res.data
                const detectedVehicleType =
                  typeof rawErrorContext === 'string'
                    ? rawErrorContext
                    : errorContext.vehicle_type || res.data.vehicle_type
                const illogicalFlowData = {
                  error_code: res.data.error_code,
                  message: res.data.error_message || res.data.message || `Error: ${res.data.error_code}`,
                  registration_number: numberToSubmit,
                  user_logged_in: errorContext.user_logged_in ?? false,
                  last_digits: errorContext.last_digits || '',
                  redirect_url: errorContext.redirect_url || null,
                  vehicle_type: detectedVehicleType || 'car',
                  ...errorContext,
                  ...res.data,
                }

                giCarContext.setIllogicalFlowData(illogicalFlowData)
                giCarContext.setProposalData(res.data)
                onError?.({
                  type: 'illogical_flow',
                  error_code: res.data.error_code,
                  data: illogicalFlowData,
                })
              } else {
                giCarContext.setErrorMessage(res.data.error_message || res.data.message || 'An error occurred')
                onError?.({
                  type: 'api_error',
                  error_code: res.data.error_code,
                  data: res.data,
                })
              }
            } else if (res.data?.ekey) {
              giCarContext.setProposalData(res.data)

              onSuccess?.(res.data)
            } else {
              giCarContext.setProposalData(res.data)
              onSuccess?.(res.data)
            }
          })
          .catch(error => {
            console.error('Error submitting registration number:', error)
            const err = JSON.stringify(error?.request?.response || error?.response?.data || {}).toUpperCase()
            if (err.includes('MODEL_UNSERVICEABLE')) {
              const errorData = error?.response?.data || error?.request?.response || {}
              const illogicalFlowData = {
                error_code: 'MODEL_UNSERVICEABLE',
                message: 'Model unserviceable',
                registration_number: numberToSubmit,
                user_logged_in: false,
                last_digits: '',
                redirect_url: null,
                vehicle_type: 'car',
                ...errorData,
              }
              giCarContext.setIllogicalFlowData(illogicalFlowData)
              giCarContext.setProposalData({
                error_code: 'MODEL_UNSERVICEABLE',
                error_context: {
                  user_logged_in: false,
                  last_digits: '',
                  redirect_url: null,
                  vehicle_type: 'car',
                },
              })
              onError?.({
                type: 'illogical_flow',
                error_code: 'MODEL_UNSERVICEABLE',
                data: illogicalFlowData,
              })
            } else if (error?.request?.status === 400) {
              try {
                const errorObject = JSON.parse(error?.request?.response || '{}')
                const errorMessage = errorObject?.error_message || 'Invalid registration number. Please try again.'
                setLocalError(errorMessage)
                giCarContext.setErrorMessage(errorMessage)

                onError?.({
                  type: 'api_error',
                  error: errorObject,
                })
              } catch (parseError) {
                const errorMessage = 'Invalid registration number. Please try again.'
                setLocalError(errorMessage)
                giCarContext.setErrorMessage(errorMessage)
                onError?.({
                  type: 'api_error',
                  error: { error_message: errorMessage },
                })
              }
            } else {
              const errorMessage = error?.message || 'An error occurred. Please try again.'
              setLocalError(errorMessage)
              giCarContext.setErrorMessage(errorMessage)

              onError?.({
                type: 'exception',
                error,
              })
            }
          })
          .finally(() => {
            giCarContext.setIsLoading(false)
          })
      } catch (error: any) {
        console.error('Unexpected error:', error)
        giCarContext.setIsLoading(false)
        const errorMessage = error?.message || 'An error occurred. Please try again.'
        setLocalError(errorMessage)
        giCarContext.setErrorMessage(errorMessage)

        onError?.({
          type: 'exception',
          error,
        })
      }
    },
    [giCarContext, mobileNumber, onSuccess, onError, regexPatternsData]
  )

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && giCarContext.registrationNumber.trim()) {
        e.preventDefault()
        void handleSubmit(undefined, 'enter')
      }
    },
    [giCarContext, handleSubmit]
  )

  return (
    <FormWidget
      registrationNumber={giCarContext.registrationNumber}
      error={localError}
      isLoading={giCarContext.isLoading}
      onChangeHandler={handleChangeHandler}
      onBlurHandler={handleBlurHandler}
      onClickHandler={() => {
        void handleSubmit(undefined, 'cta')
      }}
      onKeyPressHandler={handleKeyPress}
      formData={formData}
    />
  )
}

export default RegistrationNumber
